import i18n, { currentLocale } from '../../i18n'
import { formatPrice } from '../format'
/**
 * Logique d'affichage des tarifs refuge (dortoir / exclusif, disponibilité saisonnière).
 */

export type RefugeRentalType = 'Dortoir' | 'Exclusive' | 'Dortoir ou exclusive' | 'N/A' | string | null | undefined

export interface RefugePricingInput {
  rental_type?: RefugeRentalType
  cost?: string | null
  dormitory_price?: number | string | null
  exclusive_price?: number | string | null
  exclusive_occupation?: string | null
  price_date?: number | null
  summer_available?: boolean
  winter_available?: boolean
}

export function showsRefugeDormitoryPrice(rentalType: RefugeRentalType): boolean {
  return rentalType === 'Dortoir' || rentalType === 'Dortoir ou exclusive'
}

export function showsRefugeExclusivePrice(rentalType: RefugeRentalType): boolean {
  return rentalType === 'Exclusive' || rentalType === 'Dortoir ou exclusive'
}

export function showsRefugeExclusiveOccupation(rentalType: RefugeRentalType): boolean {
  return showsRefugeExclusivePrice(rentalType)
}

export function parseRefugePrice(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null
  const num = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(num) ? num : null
}

/** Montant en CAD affiché dans la devise choisie (Profil → Format) ; locale ou devise explicites : sans conversion. */
export function formatRefugePrice(value: number, locale = currentLocale(), currency = 'CAD'): string {
  if (currency === 'CAD' && locale === currentLocale()) return formatPrice(value, { decimals: 2 })
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

/** Saison quand une seule est cochée. */
export function getRefugeSeasonOnly(
  summerAvailable?: boolean,
  winterAvailable?: boolean,
): 'summer' | 'winter' | null {
  const summer = summerAvailable !== false
  const winter = winterAvailable !== false
  if (summer && !winter) return 'summer'
  if (!summer && winter) return 'winter'
  return null
}

/** Libellé quand une seule saison est cochée. */
export function getRefugeSeasonOnlyLabel(
  summerAvailable?: boolean,
  winterAvailable?: boolean,
): string | null {
  const season = getRefugeSeasonOnly(summerAvailable, winterAvailable)
  if (season === 'summer') return i18n.t('common:refugePricingUtils.ete_seulement')
  if (season === 'winter') return i18n.t('common:refugePricingUtils.hiver_seulement')
  return null
}

export interface RefugePriceLine {
  amount: number
  label: string
}

export function getRefugePriceLines(data: RefugePricingInput): RefugePriceLine[] {
  if (data.cost === 'no') return []

  const rentalType = data.rental_type
  const lines: RefugePriceLine[] = []

  const dormitoryAmount = parseRefugePrice(data.dormitory_price)
  const exclusiveAmount = parseRefugePrice(data.exclusive_price)

  if (!rentalType || rentalType === 'N/A') {
    if (dormitoryAmount !== null) {
      lines.push({ amount: dormitoryAmount, label: 'dortoir' })
    }
    if (exclusiveAmount !== null) {
      lines.push({ amount: exclusiveAmount, label: 'exclusive' })
    }
    return lines
  }

  if (showsRefugeDormitoryPrice(rentalType) && dormitoryAmount !== null) {
    lines.push({ amount: dormitoryAmount, label: 'dortoir' })
  }

  if (showsRefugeExclusivePrice(rentalType) && exclusiveAmount !== null) {
    lines.push({ amount: exclusiveAmount, label: 'exclusive' })
  }

  return lines
}

export function hasRefugePricingToShow(data: RefugePricingInput): boolean {
  return getRefugePriceLines(data).length > 0
}

export function hasRefugePriceDate(priceDate: number | null | undefined): boolean {
  return priceDate != null && priceDate > 0
}

export function getRefugePriceUpdateLabel(priceDate: number | null | undefined): string | null {
  return hasRefugePriceDate(priceDate) ? i18n.t('common:refugePricingUtils.mis_jour', { priceDate: priceDate ?? '' }) : null
}
